/**
 * Pre-rasterizes container-type SVGs to offscreen canvases keyed by TYPES enum.
 * Drawn as a white mask so consumers can composite-tint per icon use.
 *
 * Ported from the standalone crm-core-web POC (branch: poc/container-marker-webgl, commit 66cfff0d4).
 */
import { useEffect, useState } from 'react'

import compactorUrl from 'assets/inventory/container-type/Compactor.svg'
import frontLoadUrl from 'assets/inventory/container-type/FrontLoad.svg'
import handwashingStationUrl from 'assets/inventory/container-type/HandwashingStation.svg'
import openTopBoxUrl from 'assets/inventory/container-type/OpenTopBox.svg'
import portableToiletUrl from 'assets/inventory/container-type/PortableToilet.svg'
import rearLoadUrl from 'assets/inventory/container-type/RearLoad.svg'
import restroomTrailerUrl from 'assets/inventory/container-type/RestroomTrailer.svg'
import toterUrl from 'assets/inventory/container-type/Toter.svg'
import { TYPES } from 'components/settings/pages/inventory/container-types/settings'

const ICON_CSS_SIZE = 16
// 4× supersample (was 2×) so the icon source has roughly twice the pixel density of where it gets drawn inside the pill canvas. The downscale produces visibly sharper edges, especially on the thin lines inside the container-type SVGs.
const OVERSAMPLE = 4

const URL_BY_TYPE = {
  [TYPES.OPEN_TOP_BOX]: openTopBoxUrl,
  [TYPES.COMPACTOR]: compactorUrl,
  [TYPES.FRONT_LOAD]: frontLoadUrl,
  [TYPES.REAR_LOAD]: rearLoadUrl,
  [TYPES.TOTER]: toterUrl,
  [TYPES.PORTABLE_TOILET]: portableToiletUrl,
  [TYPES.HANDWASHING_STATION]: handwashingStationUrl,
  [TYPES.RESTROOM_TRAILER]: restroomTrailerUrl,
}

const cache = new Map()
const readyListeners = new Set()

function rasterize(url) {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => {
      const dpi = (typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2) * OVERSAMPLE
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(ICON_CSS_SIZE * dpi)
      canvas.height = Math.ceil(ICON_CSS_SIZE * dpi)
      const ctx = canvas.getContext('2d')
      ctx.scale(dpi, dpi)
      ctx.drawImage(img, 0, 0, ICON_CSS_SIZE, ICON_CSS_SIZE)
      resolve(canvas)
    }
    img.onerror = () => resolve(null)
    img.src = url
  })
}

const rasterizeWithRetry = async (type, url) => {
  const first = await rasterize(url)
  if (first) return first
  const retry = await rasterize(url)
  if (!retry) console.warn(`containerIconRaster: failed to decode SVG for type=${type} url=${url}`)
  return retry
}

Object.entries(URL_BY_TYPE).forEach(([type, url]) => {
  rasterizeWithRetry(type, url).then(canvas => {
    if (canvas) cache.set(type, canvas)
    readyListeners.forEach(fn => fn())
  })
})

// Fall back to Toter for unknown / missing types — matches the DOM ContainerMarker (get(TYPE_TO_ICON_MAP, type, ToterIcon)).
export function getContainerIconCanvas(type) {
  return cache.get(type) || cache.get(TYPES.TOTER) || null
}

export { ICON_CSS_SIZE }

export function useContainerIconsReady() {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const listener = () => setTick(t => t + 1)
    readyListeners.add(listener)
    return () => readyListeners.delete(listener)
  }, [])
  return tick
}
